// pages/User/components/UserAdsList.jsx
import { useTranslation } from 'react-i18next';
import UserAdsCard from './UserAdsCard';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { BsGridFill, BsListUl } from 'react-icons/bs';
import { useState } from 'react';

const UserAdsList = ({ ads, loading, currentPage, totalPages, onPageChange, userName }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  if (loading) {
    return (
      <div className='rounded-xl bg-white p-8 shadow-lg'>
        <div className='mb-6 flex items-center justify-between'>
          <div className='h-8 w-48 animate-pulse rounded bg-gray-200'></div>
          <div className='h-10 w-24 animate-pulse rounded bg-gray-200'></div>
        </div>

        <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
          {[...Array(6)].map((_, index) => (
            <div key={index} className='animate-pulse'>
              <div className='h-48 rounded-t-xl bg-gray-200'></div>
              <div className='space-y-3 rounded-b-xl bg-gray-100 p-4'>
                <div className='h-4 w-3/4 rounded bg-gray-200'></div>
                <div className='h-3 w-1/2 rounded bg-gray-200'></div>
                <div className='h-3 w-2/3 rounded bg-gray-200'></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!ads || ads.length === 0) {
    return (
      <div className='rounded-xl bg-white p-12 text-center shadow-lg'>
        <div className='mx-auto max-w-md'>
          <div className='mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gray-100'>
            <svg className='h-12 w-12 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
          </div>
          <h2 className='mb-3 text-2xl font-bold text-gray-800'>{t('noAdsFound')}</h2>
          <p className='mb-2 text-gray-600'>{t('userHasNoActiveAds')}</p>
          <p className='text-sm text-gray-500'>
            {userName} {t('hasNotPostedAdsYet')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='rounded-xl bg-white p-6 shadow-lg'>
      {/* Header with View Toggle */}
      <div className='mb-6 flex flex-col items-start justify-between gap-4 border-b border-gray-200 pb-6 md:flex-row md:items-center'>
        <div>
          <h2 className='mb-1 text-2xl font-bold text-gray-800'>
            {t('adsFrom')} {userName}
          </h2>
          <p className='text-gray-600'>
            {t('showing')} <span className='font-semibold text-gray-800'>{ads.length}</span> {t('of')}{' '}
            <span className='font-semibold text-gray-800'>{ads.length * totalPages}</span> {t('ads')}
          </p>
        </div>

        {/* View Mode Toggle */}
        <div className='flex items-center gap-2 rounded-lg bg-gray-100 p-1'>
          <button
            onClick={() => setViewMode('grid')}
            className={`flex items-center gap-2 rounded-md px-4 py-2 transition-colors ${
              viewMode === 'grid' ? 'bg-primary-yellow text-gray-800 shadow-sm' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <BsGridFill />
            <span className='hidden sm:inline'>{t('grid')}</span>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-2 rounded-md px-4 py-2 transition-colors ${
              viewMode === 'list' ? 'bg-primary-yellow text-gray-800 shadow-sm' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <BsListUl />
            <span className='hidden sm:inline'>{t('list')}</span>
          </button>
        </div>
      </div>

      {/* Ads Grid/List */}
      <div className={viewMode === 'grid' ? 'mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3' : 'mb-8 flex flex-col gap-4'}>
        {ads.map((ad) => (
          <UserAdsCard key={ad._id} ad={ad} viewMode={viewMode} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex flex-col items-center gap-6 border-t border-gray-200 pt-6'>
          {/* Page Info */}
          <div className='text-center'>
            <p className='text-sm text-gray-600'>
              {t('page')} <span className='font-semibold text-gray-800'>{currentPage}</span> {t('of')}{' '}
              <span className='font-semibold text-gray-800'>{totalPages}</span>
            </p>
          </div>

          {/* Pagination Controls */}
          <div className='flex items-center justify-center gap-2'>
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className='group rounded-full bg-gray-100 p-3 transition-colors hover:bg-primary-yellow disabled:cursor-not-allowed disabled:bg-gray-200'
              aria-label='Previous page'
            >
              <FaChevronLeft className='text-gray-700 group-hover:text-gray-900' />
            </button>

            <div className='flex items-center gap-2'>
              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                const showPage =
                  pageNumber === 1 || pageNumber === totalPages || (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);

                if (!showPage) {
                  if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                    return (
                      <span key={pageNumber} className='px-2 font-medium text-gray-400'>
                        ...
                      </span>
                    );
                  }
                  return null;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => onPageChange(pageNumber)}
                    className={`h-11 w-11 rounded-full font-semibold transition-all duration-200 ${
                      currentPage === pageNumber
                        ? 'scale-110 bg-primary-yellow text-gray-800 shadow-lg'
                        : 'bg-gray-100 text-gray-600 hover:scale-105 hover:bg-gray-200'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className='group rounded-full bg-gray-100 p-3 transition-colors hover:bg-primary-yellow disabled:cursor-not-allowed disabled:bg-gray-200'
              aria-label='Next page'
            >
              <FaChevronRight className='text-gray-700 group-hover:text-gray-900' />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAdsList;
